/**
 * TrustGrid Evidence Graph - D3.js Interactive Visualization
 * 3-Level Hierarchy: Frameworks → Controls → Evidence
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as d3 from 'd3';
import { Drawer, Card, Tag, Descriptions, Button, Space } from 'antd';
import { CloseOutlined, ExpandOutlined, DownloadOutlined } from '@ant-design/icons';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import { listControls, getControlResults, getLatestScan } from '../api/trustgridApi';
import ProjectSelector from './ProjectSelector';
import { LoadingState } from '../../../components/common';
import { showError } from '../../../utils/notifications';
import { handleCrossOrgError } from '../utils/errorHandlers';
import { colors, spacing } from '../../../theme/designTokens';

function EvidenceGraph() {
  const navigate = useNavigate();
  const { selectedProjectId, selectedProject, selectProject } = useTrustGrid();
  const projectId = selectedProjectId || 3;
  const svgRef = useRef();
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [graphData, setGraphData] = useState(null);

  useEffect(() => {
    fetchGraphData();
  }, [projectId]);

  useEffect(() => {
    if (graphData && svgRef.current) {
      renderGraph();
    }
  }, [graphData]);

  const fetchGraphData = async () => {
    setLoading(true);
    try {
      const [controlsRes, resultsRes, scanRes] = await Promise.all([
        listControls(),
        getControlResults(projectId).catch(() => ({ data: { results: [] } })),
        getLatestScan(projectId).catch(() => ({ data: null }))
      ]);

      const controls = controlsRes.data.controls || [];
      const results = resultsRes.data.results || [];
      const scan = scanRes.data;

      // Build control results map
      const controlResultsMap = {};
      results.forEach(r => {
        controlResultsMap[r.control_code] = r;
      });

      // Define framework mappings (which controls belong to which frameworks)
      const frameworkMappings = {
        'EU AI Act': ['CTRL_01', 'CTRL_02', 'CTRL_06', 'CTRL_09'],
        'GDPR': ['CTRL_01', 'CTRL_03', 'CTRL_09'],
        'ISO 42001': ['CTRL_01', 'CTRL_02', 'CTRL_03', 'CTRL_04', 'CTRL_05', 'CTRL_06', 'CTRL_07', 'CTRL_08', 'CTRL_09', 'CTRL_10'],
        'SOC 2': ['CTRL_04', 'CTRL_05', 'CTRL_08']
      };

      // Build hierarchical data structure
      const nodes = [];
      const links = [];
      let nodeId = 0;

      // Level 1: Frameworks
      const frameworkNodes = {};
      Object.keys(frameworkMappings).forEach(framework => {
        const frameworkControls = frameworkMappings[framework];
        const frameworkResults = frameworkControls.map(code => controlResultsMap[code]).filter(Boolean);
        const passingCount = frameworkResults.filter(r => r.status === 'pass').length;
        const totalCount = frameworkResults.length;
        const compliance = totalCount > 0 ? Math.round((passingCount / totalCount) * 100) : 0;

        const node = {
          id: nodeId++,
          name: framework,
          type: 'framework',
          compliance,
          level: 0,
          color: compliance >= 70 ? colors.status.pass : compliance >= 50 ? colors.status.warning : colors.status.fail
        };
        frameworkNodes[framework] = node;
        nodes.push(node);
      });

      // Level 2: Controls
      const controlNodes = {};
      controls.forEach(control => {
        const result = controlResultsMap[control.code];
        const node = {
          id: nodeId++,
          name: control.code,
          type: 'control',
          title: control.title,
          severity: control.severity,
          status: result?.status || 'unknown',
          confidence: result?.confidence || 0,
          level: 1,
          color: result?.status === 'pass' ? colors.status.pass : result?.status === 'fail' ? colors.status.fail : colors.text.disabled
        };
        controlNodes[control.code] = node;
        nodes.push(node);

        // Link controls to frameworks
        Object.entries(frameworkMappings).forEach(([framework, codes]) => {
          if (codes.includes(control.code)) {
            links.push({
              source: frameworkNodes[framework].id,
              target: node.id,
              type: 'framework-control'
            });
          }
        });
      });

      // Level 3: Evidence (sample - in production, fetch from backend)
      if (scan) {
        const evidenceCount = Math.min(controls.length * 2, 30); // Limit evidence nodes
        for (let i = 0; i < evidenceCount; i++) {
          const randomControl = controls[Math.floor(Math.random() * controls.length)];
          const evidenceNode = {
            id: nodeId++,
            name: `evt_${1000 + i}`,
            type: 'evidence',
            eventType: ['API Call', 'Data Access', 'Model Training', 'User Action'][Math.floor(Math.random() * 4)],
            timestamp: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
            level: 2,
            color: colors.primary[500]
          };
          nodes.push(evidenceNode);

          // Link evidence to controls
          if (controlNodes[randomControl.code]) {
            links.push({
              source: controlNodes[randomControl.code].id,
              target: evidenceNode.id,
              type: 'control-evidence'
            });
          }
        }
      }

      setGraphData({ nodes, links });
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'EvidenceGraph');
      if (!wasHandled) {
        console.error('Failed to fetch graph data:', error);
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderGraph = () => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = svgRef.current.clientWidth;
    const height = 600;

    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g');

    // Add zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.5, 3])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Create simulation
    const simulation = d3.forceSimulation(graphData.nodes)
      .force('link', d3.forceLink(graphData.links).id(d => d.id).distance(100))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('x', d3.forceX(d => {
        // Horizontal positioning based on level
        if (d.level === 0) return width * 0.2;
        if (d.level === 1) return width * 0.5;
        return width * 0.8;
      }).strength(1))
      .force('y', d3.forceY(height / 2).strength(0.1))
      .force('collision', d3.forceCollide().radius(30));

    // Draw links
    const link = g.append('g')
      .selectAll('line')
      .data(graphData.links)
      .enter()
      .append('line')
      .attr('stroke', d => d.type === 'framework-control' ? colors.primary[700] : colors.border.medium)
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', 1);

    // Draw nodes
    const node = g.append('g')
      .selectAll('g')
      .data(graphData.nodes)
      .enter()
      .append('g')
      .attr('cursor', 'pointer')
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended));

    // Node circles
    node.append('circle')
      .attr('r', d => d.type === 'framework' ? 20 : d.type === 'control' ? 12 : 8)
      .attr('fill', d => d.color)
      .attr('stroke', colors.background.primary)
      .attr('stroke-width', 2);

    // Node labels
    node.append('text')
      .text(d => d.name)
      .attr('x', 0)
      .attr('y', d => d.type === 'framework' ? 30 : d.type === 'control' ? 20 : 16)
      .attr('text-anchor', 'middle')
      .attr('font-size', d => d.type === 'framework' ? '12px' : '10px')
      .attr('font-weight', d => d.type === 'framework' ? 'bold' : 'normal')
      .attr('fill', colors.text.primary);

    // Click handler
    node.on('click', (event, d) => {
      event.stopPropagation();
      setSelectedNode(d);
      setDrawerOpen(true);
    });

    // Update positions on simulation tick
    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    function dragstarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedNode(null);
  };

  const renderDrawerContent = () => {
    if (!selectedNode) return null;

    switch (selectedNode.type) {
      case 'framework':
        return (
          <div>
            <h3 style={{ marginBottom: spacing.md }}>{selectedNode.name}</h3>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Type">Framework</Descriptions.Item>
              <Descriptions.Item label="Compliance Score">
                <Tag color={selectedNode.compliance >= 70 ? colors.status.pass : selectedNode.compliance >= 50 ? colors.status.warning : colors.status.fail}>
                  {selectedNode.compliance}%
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Level">Level 1 - Framework</Descriptions.Item>
            </Descriptions>
            <p style={{ marginTop: spacing.md, color: colors.text.secondary }}>
              This framework aggregates compliance based on multiple controls.
            </p>
          </div>
        );

      case 'control':
        return (
          <div>
            <h3 style={{ marginBottom: spacing.md }}>{selectedNode.name}</h3>
            <h4 style={{ color: colors.text.secondary, marginBottom: spacing.md }}>{selectedNode.title}</h4>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Type">Control</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={selectedNode.status === 'pass' ? colors.status.pass : selectedNode.status === 'fail' ? colors.status.fail : 'default'}>
                  {selectedNode.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Severity">
                <Tag color={selectedNode.severity === 'critical' ? colors.severity.critical.main : selectedNode.severity === 'high' ? colors.severity.high.main : 'default'}>
                  {selectedNode.severity}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Confidence">{selectedNode.confidence}%</Descriptions.Item>
              <Descriptions.Item label="Level">Level 2 - Control</Descriptions.Item>
            </Descriptions>
          </div>
        );

      case 'evidence':
        return (
          <div>
            <h3 style={{ marginBottom: spacing.md }}>Evidence: {selectedNode.name}</h3>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Type">Evidence Event</Descriptions.Item>
              <Descriptions.Item label="Event Type">
                <Tag color={colors.primary[500]}>{selectedNode.eventType}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Timestamp">
                {new Date(selectedNode.timestamp).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Level">Level 3 - Evidence</Descriptions.Item>
            </Descriptions>
            <p style={{ marginTop: spacing.md, color: colors.text.secondary }}>
              This evidence supports control evaluation.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  // Export graph data to JSON
  const handleExportJSON = () => {
    if (!graphData) return;

    const exportData = {
      project: selectedProject?.name || 'Unknown',
      exported_at: new Date().toISOString(),
      graph_type: '3-level-hierarchy',
      nodes: graphData.nodes,
      links: graphData.links
    };

    const jsonContent = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `evidence-graph-${selectedProject?.name || 'export'}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Export graph as SVG
  const handleExportSVG = () => {
    if (!svgRef.current) return;

    const svgElement = svgRef.current;
    const serializer = new XMLSerializer();
    let source = serializer.serializeToString(svgElement);

    // Add XML declaration
    source = '<?xml version="1.0" standalone="no"?>\r\n' + source;

    // Convert to blob
    const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `evidence-graph-${selectedProject?.name || 'export'}-${new Date().toISOString().split('T')[0]}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div>
      <ProjectSelector />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md }}>
        <div>
          <h2 style={{ marginBottom: spacing.xs, color: colors.text.primary }}>Evidence Graph</h2>
          <p style={{ color: colors.text.secondary, margin: 0 }}>
            Interactive 3-Level Traceability: Frameworks → Controls → Evidence
          </p>
        </div>
        <Space size={spacing.xs}>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportJSON}
          >
            Export JSON
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportSVG}
          >
            Export SVG
          </Button>
          <Button
            type="primary"
            icon={<ExpandOutlined />}
            onClick={() => {
              if (svgRef.current) {
                const svg = d3.select(svgRef.current);
                svg.transition().duration(750).call(
                  d3.zoom().transform,
                  d3.zoomIdentity
                );
              }
            }}
          >
            Reset Zoom
          </Button>
        </Space>
      </div>

      {loading ? (
        <LoadingState type="spinner" />
      ) : (
        <Card style={{ marginTop: spacing.md }}>
          <div style={{ marginBottom: spacing.sm, display: 'flex', gap: spacing.md, fontSize: '12px' }}>
            <div>
              <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: colors.status.pass, borderRadius: '50%', marginRight: spacing.xs }}></span>
              Passing / High Compliance
            </div>
            <div>
              <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: colors.status.warning, borderRadius: '50%', marginRight: spacing.xs }}></span>
              Medium Compliance
            </div>
            <div>
              <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: colors.status.fail, borderRadius: '50%', marginRight: spacing.xs }}></span>
              Failing / Low Compliance
            </div>
            <div>
              <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: colors.primary[500], borderRadius: '50%', marginRight: spacing.xs }}></span>
              Evidence
            </div>
          </div>
          <svg
            ref={svgRef}
            style={{
              width: '100%',
              height: '600px',
              border: `1px solid ${colors.border.light}`,
              borderRadius: '4px',
              backgroundColor: colors.background.secondary
            }}
          />
          <p style={{ marginTop: spacing.sm, fontSize: '12px', color: colors.text.secondary }}>
            💡 Click any node to view details. Drag nodes to rearrange. Scroll to zoom.
          </p>
        </Card>
      )}

      <Drawer
        title={selectedNode ? `${selectedNode.type.charAt(0).toUpperCase() + selectedNode.type.slice(1)} Details` : 'Details'}
        placement="right"
        onClose={handleCloseDrawer}
        open={drawerOpen}
        width={500}
        closeIcon={<CloseOutlined />}
      >
        {renderDrawerContent()}
      </Drawer>
    </div>
  );
}

export default EvidenceGraph;
